import { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Select, Spin } from "antd";
import { SlLocationPin } from "react-icons/sl";
import { BsShieldFillCheck, BsShieldFillExclamation } from "react-icons/bs";
import apiClient from "../../../../helpers/apiClient";

// Memoized icon styles to prevent object recreation
const ICON_STYLES = {
    enabled: { color: "#22c55e", fontSize: "0.9rem" },
    disabled: { color: "#f59e0b", fontSize: "0.9rem" },
    suffix: { fontSize: "14px" },
};

const FLEX_CONTAINER_STYLE = { display: "flex", alignItems: "center", gap: "0.5rem" };

const SelectLocation = ({ selectedLocation, setSelectedLocation, t, isCompanyInsuranceEnabled = true }) => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchLocations = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await apiClient.get("/api/location/list?limit=200");

            const locationList = data?.data ?? [];
            if (locationList.length > 0) {
                const result = locationList.map((loc) => {
                    const cityState = `${loc.city}, ${loc.state}`;
                    return {
                        value: String(loc.id ?? loc._id ?? ""),
                        label: isCompanyInsuranceEnabled ? (
                            <div style={FLEX_CONTAINER_STYLE}>
                                {loc.isInsuranceEnabled ? (
                                    <BsShieldFillCheck style={ICON_STYLES.enabled} title="Insurance enabled" />
                                ) : (
                                    <BsShieldFillExclamation
                                        style={ICON_STYLES.disabled}
                                        title="Insurance not enabled"
                                    />
                                )}
                                <span>{cityState}</span>
                            </div>
                        ) : (
                            <span>{cityState}</span>
                        ),
                        searchLabel: cityState,
                        cityState,
                        isInsuranceEnabled: loc.isInsuranceEnabled,
                        originalLocation: loc,
                    };
                });
                setLocations(result);
            } else {
                setLocations([]);
            }
        } catch (_error) {
            setLocations([]);
        } finally {
            setLoading(false);
        }
    }, [isCompanyInsuranceEnabled]);

    const handleLocationChange = useCallback(
        (value, option) => {
            setSelectedLocation({
                locationId: value,
                location: option.cityState,
                locationData: option.originalLocation,
            });
        },
        [setSelectedLocation],
    );

    const selectedInsuranceStatus = useMemo(() => {
        if (!selectedLocation?.locationId) return false;
        return locations.find((loc) => loc.value === selectedLocation.locationId)?.isInsuranceEnabled ?? false;
    }, [locations, selectedLocation?.locationId]);

    const filterOption = useCallback(
        (input, option) => (option?.searchLabel ?? "").toLowerCase().includes(input.toLowerCase()),
        [],
    );

    const labelRender = useCallback(
        () => (
            <div style={FLEX_CONTAINER_STYLE}>
                {isCompanyInsuranceEnabled &&
                    (selectedInsuranceStatus ? (
                        <BsShieldFillCheck style={ICON_STYLES.enabled} />
                    ) : (
                        <BsShieldFillExclamation style={ICON_STYLES.disabled} />
                    ))}
                <span>{selectedLocation?.location}</span>
            </div>
        ),
        [isCompanyInsuranceEnabled, selectedInsuranceStatus, selectedLocation?.location],
    );

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    return (
        <div>
            <div className="flex items-start mb-2">
                <p className="text-md font-medium mb-2">{`${t("company")} ${t("location")}`}</p>
            </div>
            <Select
                showSearch
                placeholder={loading ? t("loading") : t("location")}
                onChange={handleLocationChange}
                value={loading ? undefined : selectedLocation?.locationId || undefined}
                className="w-full"
                options={locations}
                loading={loading}
                disabled={loading}
                filterOption={filterOption}
                suffixIcon={loading ? undefined : <SlLocationPin style={ICON_STYLES.suffix} />}
                optionLabelProp="cityState"
                labelRender={loading ? undefined : labelRender}
                notFoundContent={
                    loading ? (
                        <div className="flex items-center justify-center gap-2 py-6">
                            <Spin size="small" />
                            <span className="text-sm text-gray-500">{t("loading")}</span>
                        </div>
                    ) : null
                }
            />
        </div>
    );
};

SelectLocation.propTypes = {
    selectedLocation: PropTypes.object,
    setSelectedLocation: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    isCompanyInsuranceEnabled: PropTypes.bool,
};

export default SelectLocation;
